import { PageHeader, SectionLabel, Card, Stat, SampleBanner, StatusPill } from '../components/ui.js';

interface CheckItem {
  id: number;
  category: string;
  name: string;
  status: 'pass' | 'fix' | 'fail' | 'na';
  detail: string;
}
interface PendingFix {
  id: string;
  account: string;
  audit_id: number;
  title: string;
  severity: 'high' | 'med' | 'low';
  detected_at: string;
  fix_strategy: string;
}

const CHECKS: CheckItem[] = [
  { id: 1, category: 'Indexability', name: 'robots.txt allows crawling', status: 'pass', detail: 'No disallow on key paths' },
  { id: 2, category: 'Indexability', name: 'XML sitemap present + submitted', status: 'pass', detail: '77 URLs, last submitted Apr 28' },
  { id: 3, category: 'Indexability', name: 'No noindex on revenue pages', status: 'pass', detail: 'All service pages indexed' },
  { id: 4, category: 'Schema', name: 'LocalBusiness / Dentist schema valid', status: 'pass', detail: 'Schema.org Dentist + Place + GeoCoordinates' },
  { id: 5, category: 'Schema', name: 'Breadcrumb markup on all pages', status: 'pass', detail: 'BreadcrumbList present' },
  { id: 6, category: 'Schema', name: 'FAQPage where applicable', status: 'fix', detail: '3 service pages missing FAQPage schema' },
  { id: 7, category: 'Schema', name: 'Speakable for AI engines', status: 'pass', detail: 'Speakable specs on all blog posts' },
  { id: 8, category: 'Performance', name: 'LCP under 2.5s', status: 'pass', detail: 'p75 LCP = 1.8s' },
  { id: 9, category: 'Performance', name: 'INP under 200ms', status: 'pass', detail: 'p75 INP = 142ms' },
  { id: 10, category: 'Performance', name: 'CLS under 0.1', status: 'pass', detail: 'p75 CLS = 0.04' },
  { id: 11, category: 'Mobile', name: 'Mobile-friendly viewport', status: 'pass', detail: 'meta viewport present' },
  { id: 12, category: 'Mobile', name: 'Tap targets ≥ 44px', status: 'pass', detail: 'All clickable elements meet AAA' },
  { id: 13, category: 'Mobile', name: 'No horizontal scroll', status: 'pass', detail: 'Verified across 5 viewports' },
  { id: 14, category: 'Content', name: 'Title tags 50-60 chars', status: 'fix', detail: '4 pages over 60 chars' },
  { id: 15, category: 'Content', name: 'Meta descriptions 140-160 chars', status: 'pass', detail: 'All within range' },
  { id: 16, category: 'Content', name: 'H1 present + unique', status: 'pass', detail: 'Single H1 per page' },
  { id: 17, category: 'Content', name: 'No thin content (<300 words)', status: 'fix', detail: 'Insurance page = 218 words' },
  { id: 18, category: 'Internal Linking', name: 'No orphan pages', status: 'pass', detail: 'Every page reachable in ≤3 clicks' },
  { id: 19, category: 'Internal Linking', name: 'Breadcrumbs on inner pages', status: 'pass', detail: 'Verified' },
  { id: 20, category: 'Internal Linking', name: 'Pillar-cluster links present', status: 'fail', detail: 'Implants pillar missing 4/8 cluster links' },
  { id: 21, category: 'Trust', name: 'NAP consistency across web', status: 'pass', detail: 'Verified across GBP + Yelp + Apple Maps' },
  { id: 22, category: 'Trust', name: 'SSL valid + HSTS', status: 'pass', detail: 'TLS 1.3, HSTS preload eligible' },
  { id: 23, category: 'Trust', name: 'Privacy + terms reachable', status: 'pass', detail: 'Footer links present' },
  { id: 24, category: 'Local', name: 'GBP claimed + verified', status: 'pass', detail: 'All 3 locations verified' },
  { id: 25, category: 'Local', name: 'Reviews ≥ 4.5★ avg', status: 'pass', detail: 'Avg 4.8 across locations' },
];

const PENDING_FIXES: PendingFix[] = [
  { id: 'f-001', account: 'Glisten Gilbert', audit_id: 20, title: 'Add 4 missing cluster links from Implants pillar', severity: 'high', detected_at: '2026-04-28T05:00:00Z', fix_strategy: 'Auto-inject internal links via WP REST on next daily_apply_approved_fixes run' },
  { id: 'f-002', account: 'Glisten Gilbert', audit_id: 14, title: 'Trim 4 title tags exceeding 60 chars', severity: 'med', detected_at: '2026-04-28T05:00:00Z', fix_strategy: 'LLM-rewrite title tags preserving keyword + brand' },
  { id: 'f-003', account: 'Glisten Mesa', audit_id: 17, title: 'Expand /insurance page from 218 → 600+ words', severity: 'med', detected_at: '2026-04-28T05:00:00Z', fix_strategy: 'Generate insurance/coverage detail (AHCCCS, Delta Dental, Cigna, MetLife)' },
  { id: 'f-004', account: 'Glisten Glendale', audit_id: 6, title: 'Add FAQPage schema to /sedation-dentistry', severity: 'low', detected_at: '2026-04-28T05:00:00Z', fix_strategy: 'Inject JSON-LD via theme function' },
  { id: 'f-005', account: 'Glisten Glendale', audit_id: 6, title: 'Add FAQPage schema to /implants', severity: 'low', detected_at: '2026-04-28T05:00:00Z', fix_strategy: 'Inject JSON-LD via theme function' },
];

export function AuditsPage() {
  const passed = CHECKS.filter((c) => c.status === 'pass').length;
  const needsFix = CHECKS.filter((c) => c.status === 'fix').length;
  const failed = CHECKS.filter((c) => c.status === 'fail').length;

  const byCategory = CHECKS.reduce((acc, c) => {
    (acc[c.category] ??= []).push(c);
    return acc;
  }, {} as Record<string, CheckItem[]>);

  return (
    <div className="space-y-12">
      <PageHeader
        eyebrow="Audits"
        title={<>25-point weekly health <em className="text-cream/80">check.</em></>}
        subtitle="Every Sunday Bloom audits each location against 25 SEO + AI-discoverability checks. Anything failing or fixable goes into the auto-fix queue."
      />
      <SampleBanner />

      <section>
        <SectionLabel>Last audit (Apr 28)</SectionLabel>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Stat label="Passed" value={passed} hint={`of ${CHECKS.length}`} tone="ok" />
          <Stat label="Needs fix" value={needsFix} hint="auto-fix queued" tone={needsFix > 0 ? 'warn' : 'ok'} />
          <Stat label="Failed" value={failed} hint="manual review" tone={failed > 0 ? 'bad' : 'ok'} />
          <Stat label="Health score" value={Math.round((passed / CHECKS.length) * 100)} hint="of 100" tone="ok" />
        </div>
      </section>

      <section>
        <SectionLabel>Pending fixes ({PENDING_FIXES.length})</SectionLabel>
        <div className="space-y-3">
          {PENDING_FIXES.map((f) => (
            <Card key={f.id}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <StatusPill tone={f.severity === 'high' ? 'bad' : f.severity === 'med' ? 'warn' : 'neutral'}>
                      {f.severity}
                    </StatusPill>
                    <span className="text-[11px] text-cream/45">{f.account} · check #{f.audit_id}</span>
                  </div>
                  <div className="text-cream/90">{f.title}</div>
                  <div className="text-[11px] text-cream/55 mt-2">{f.fix_strategy}</div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button type="button" className="text-[11px] uppercase tracking-[0.18em] px-3 py-1.5 rounded-full bg-cream/[0.08] text-cream hover:bg-cream/[0.14] border border-cream/[0.16] transition-colors">Approve</button>
                  <button type="button" className="text-[11px] uppercase tracking-[0.18em] px-3 py-1.5 rounded-full text-cream/60 hover:text-cream border border-cream/[0.12] hover:border-cream/[0.3] transition-colors">Snooze</button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </section>

      <section>
        <SectionLabel>Full checklist</SectionLabel>
        <div className="space-y-6">
          {Object.entries(byCategory).map(([category, items]) => (
            <Card key={category}>
              <div className="text-[10px] uppercase tracking-[0.22em] text-cream/40 mb-4">{category}</div>
              <div className="space-y-3">
                {items.map((c) => (
                  <div key={c.id} className="flex items-start justify-between gap-4 py-2 border-b border-cream/[0.04] last:border-0">
                    <div className="flex items-start gap-3">
                      <span className="text-[11px] text-cream/35 tabular-nums w-6 mt-0.5">{c.id}.</span>
                      <div>
                        <div className="text-cream/85 text-sm">{c.name}</div>
                        <div className="text-[11px] text-cream/45 mt-0.5">{c.detail}</div>
                      </div>
                    </div>
                    <StatusPill tone={c.status === 'pass' ? 'ok' : c.status === 'fix' ? 'warn' : c.status === 'fail' ? 'bad' : 'neutral'}>
                      {c.status === 'pass' ? '✓ pass' : c.status === 'fix' ? 'fix' : c.status === 'fail' ? 'fail' : 'n/a'}
                    </StatusPill>
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>
      </section>
    </div>
  );
}
